import React from 'react';
import { CircularProgress, Tooltip, IconButton, Fab } from '@mui/material';
import { FileDownload as FileDownloadIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { Page, Text, View, Document, StyleSheet, Font, usePDF, Image } from '@react-pdf/renderer';

// TODO: confirmar la web oficial de KOD.SOLUTIONS a mostrar en el pie de los informes
const FOOTER_WEBSITE = 'kodsolutions.com';

// ─── Brand tokens ────────────────────────────────────────────────────────────
const BRAND = {
  primary:        '#08000a',
  primaryLight:   '#2d2a6e',
  primaryDark:    '#110f2c',
  secondary:      '#E8320A',
  secondaryLight: '#5DCAA5',
  secondaryDark:  '#0F6E56',
  tertiary:       '#E8320A',
  tertiaryLight:  '#FF6040',
  neutralLight:   '#F1EFE8',
  neutralDark:    '#2C2C2A',
  white:          '#FFFFFF',
  rowAlt:         '#EBF6F1',   // very light tint of secondary for zebra rows
  rowBase:        '#F8F7F4',
};

// ─── PDF Styles ───────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    backgroundColor: BRAND.white,
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 28,
    fontFamily: 'Helvetica',
  },

  // ── Header band ──
  headerBand: {
    backgroundColor: BRAND.primary,
    marginHorizontal: -24,
    paddingHorizontal: 28,
    paddingTop: 18,
    paddingBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoMark: {
    width: 28,
    height: 28,
    backgroundColor: BRAND.secondary,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoMarkInner: {
    width: 14,
    height: 14,
    backgroundColor: BRAND.white,
    borderRadius: 2,
  },
  brandName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 18,
    color: BRAND.white,
    letterSpacing: 1.5,
  },
  brandTagline: {
    fontSize: 7,
    color: BRAND.secondaryLight,
    letterSpacing: 0.8,
    marginTop: 1,
  },
  headerMeta: {
    alignItems: 'flex-end',
  },
  headerDate: {
    fontSize: 7,
    color: BRAND.neutralLight,
    opacity: 0.7,
  },
  headerReport: {
    fontSize: 9,
    color: BRAND.secondaryLight,
    fontFamily: 'Helvetica-Bold',
    marginTop: 2,
  },

  // ── Accent strip under header ──
  accentStrip: {
    height: 3,
    marginHorizontal: -24,
    marginBottom: 4,
    flexDirection: 'row',
  },
  accentSegment1: { flex: 3, backgroundColor: BRAND.secondary },
  accentSegment2: { flex: 1, backgroundColor: BRAND.tertiary },

  // ── Table ──
  tableWrapper: {
    width: '100%',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: BRAND.primaryLight,
    borderRadius: 4,
    marginBottom: 2,
    overflow: 'hidden',
  },
  tableHeaderCell: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.white,
    padding: '5px 6px',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  tableRowEven: {
    flexDirection: 'row',
    backgroundColor: BRAND.rowBase,
    minHeight: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E4E2DA',
  },
  tableRowOdd: {
    flexDirection: 'row',
    backgroundColor: BRAND.rowAlt,
    minHeight: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#D5EDE5',
  },
  tableCell: {
    fontSize: 7,
    color: BRAND.neutralDark,
    padding: '4px 6px',
    overflow: 'hidden',
  },

  // ── Footer ──
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 24,
    right: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#D5D3CC',
    paddingTop: 5,
  },
  footerLeft: {
    fontSize: 6.5,
    color: '#999',
  },
  footerRight: {
    fontSize: 6.5,
    color: '#999',
  },
  footerBrand: {
    color: BRAND.secondary,
    fontFamily: 'Helvetica-Bold',
  },
  headerLogoImage: {
    width: 28,
    height: 28,
    borderRadius: 6,
    objectFit: 'contain',
  },
});

// ─── PDF Document ─────────────────────────────────────────────────────────────
const StockifyDocument = ({ data, headersColumn, reportTitle = 'Reporte', clientLogo = undefined }) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('es-EC', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

  const visibleCols = headersColumn.filter(c => c.show);

  return (
    <Document title={`OnAir – ${reportTitle}`} author="OnAir">
      <Page size="A4" orientation="landscape" style={styles.page} wrap>

        {/* ── Header ── */}
        <View style={styles.headerBand} fixed>
          <View style={styles.brandRow}>
            {clientLogo ? (
              <Image style={styles.headerLogoImage} src={clientLogo} />
            ) : (
              <View style={styles.logoMark}>
                <View style={styles.logoMarkInner} />
              </View>
            )}
            <View>
              <Text style={styles.brandName}>OnAir</Text>
              <Text style={styles.brandTagline}>Gestión inteligente de menciones</Text>
            </View>
          </View>
          <View style={styles.headerMeta}>
            <Text style={styles.headerReport}>{reportTitle.toUpperCase()}</Text>
            <Text style={styles.headerDate}>{dateStr} · {timeStr}</Text>
            <Text style={styles.headerDate}>{data.length} registro{data.length !== 1 ? 's' : ''}</Text>
          </View>
        </View>

        {/* ── Accent strip ── */}
        <View style={styles.accentStrip} fixed>
          <View style={styles.accentSegment1} />
          <View style={styles.accentSegment2} />
        </View>

        {/* ── Table ── */}
        <View style={styles.tableWrapper}>
          {/* Header row */}
          <View style={styles.tableHeaderRow} fixed>
            {visibleCols.map((col, i) => (
              <View key={`hcol_${i}`} style={{ width: col.width }}>
                <Text style={[styles.tableHeaderCell, { textAlign: col.align || 'left' }]}>
                  {col.label}
                </Text>
              </View>
            ))}
          </View>

          {/* Data rows */}
          {data.map((row, rowIdx) => (
            <View
              key={`row_${rowIdx}`}
              style={rowIdx % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd}
              wrap={false}
            >
              {visibleCols.map((col, colIdx) => {
                const rawSize = col.fontSize ? parseInt(col.fontSize) : 10;
                const fontSize = Math.max(rawSize - 3, 6);
                return (
                  <View key={`cell_${rowIdx}_${colIdx}`} style={{ width: col.width }}>
                    <Text
                      style={[
                        styles.tableCell,
                        {
                          fontSize,
                          fontFamily: col.bolder ? 'Helvetica-Bold' : 'Helvetica',
                          textAlign: col.align || 'left',
                        },
                      ]}
                    >
                      {row[col.id] != null ? String(row[col.id]) : '–'}
                    </Text>
                  </View>
                );
              })}
            </View>
          ))}
        </View>

        {/* ── Footer ── */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerLeft}>
            {'Creado por '}
            <Text style={styles.footerBrand}>KOD.SOLUTIONS</Text>
            {`  ·  ${FOOTER_WEBSITE}`}
          </Text>
          <Text
            style={styles.footerRight}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

// ─── Exported Component ───────────────────────────────────────────────────────
export const PdfElement = ({ data, headersColumn, HandleClickPdf, isSmall, reportTitle, clientLogo = undefined }) => {
 const [instance, update] = usePDF({
    document: (
      <StockifyDocument
        data={data}
        headersColumn={headersColumn}
        reportTitle={reportTitle}
        clientLogo={clientLogo}
      />
    ),
  });

  // Regenera el PDF cuando cambian los datos, headers, el título o el logo
  React.useEffect(() => {
    update(
      <StockifyDocument
        data={data}
        headersColumn={headersColumn}
        reportTitle={reportTitle}
        clientLogo={clientLogo}
      />,
    );
  }, [data, headersColumn, reportTitle, clientLogo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading ──
  if (instance.loading) {
    return (
      <CircularProgress
        size={isSmall ? 18 : 28}
        thickness={5}
        sx={{ color: '#E8320A' }}
      />
    );
  }

  // ── Error ──
  if (instance.error) {
    console.error('[PdfElement]', instance.error);
    return (
      <Tooltip title={`Error: ${instance.error}`} arrow>
        <span style={{ fontSize: 12, color: '#E8320A' }}>⚠ PDF error</span>
      </Tooltip>
    );
  }

  // ── Small variant (icon button) ──
  if (isSmall) {
    return (
      <Tooltip arrow title="Exportar PDF" placement="top">
        <IconButton
          href={instance.url}
          onClick={HandleClickPdf}
          target="_blank"
          size="small"
          sx={{
            backgroundColor: '#F1EFE8',
            color: '#E8320A',
            border: '1px solid #E8320A22',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#E8320A',
              color: '#fff',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px #E8320A44',
            },
          }}
        >
          <PictureAsPdfIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  // ── Full FAB variant ──
  return (
    <Tooltip arrow title="Exportar PDF" placement="top">
      <Fab
        href={instance.url}
        onClick={HandleClickPdf}
        target="_blank"
        size="small"
        sx={{
          background: 'linear-gradient(135deg, #E8320A 0%, #0F6E56 100%)',
          color: '#fff',
          boxShadow: '0 4px 14px #E8320A66',
          transition: 'all 0.25s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, #5DCAA5 0%, #E8320A 100%)',
            transform: 'translateY(-2px) scale(1.05)',
            boxShadow: '0 6px 20px #E8320A88',
          },
        }}
      >
        <FileDownloadIcon />
      </Fab>
    </Tooltip>
  );
};